/**
 * Program schedule data.
 *
 * Kept as a plain typed constant now so the schedule page can render a real
 * HTML table instead of an image. Later this can be moved to the database
 * (e.g. a `program_schedule` table with admin-only RLS) and loaded in the
 * route's loader — the UI in `screens/schedule.tsx` only depends on the
 * `ScheduleTable` shape below, not on the source.
 */

export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri";

export const DAY_ORDER: DayKey[] = ["mon", "tue", "wed", "thu", "fri"];

export type CellTone = "class" | "highlight" | "closed" | "empty";

/**
 * A rendered cell in the schedule grid.
 *
 * - `rowSpan` merges this cell with rows below it (e.g. Wednesday block).
 * - `skip` marks a cell that is covered by a rowSpan above and should NOT
 *   be rendered as its own <td>.
 * - `labelKeys` are translation keys under `programsPage.label.*`. Unknown
 *   keys fall back to the raw string so future admin-entered free text
 *   also renders.
 */
export type ScheduleCell = {
  tone: CellTone;
  labelKeys?: string[];
  subLabel?: string;
  rowSpan?: number;
  skip?: boolean;
};

export type ScheduleRow = {
  /** Displayed in the leftmost column. Empty string → rendered as empty row. */
  time: string;
  cells: Record<DayKey, ScheduleCell>;
};

export type ScheduleTable = {
  rows: ScheduleRow[];
};

/** Default schedule, matches the printed gym timetable:
 *  Mon/Tue/Thu: 3 wrestling blocks with a 40-min break between 6:20 and 7:00.
 *  Wed: 7:00–9:00 sparring + beginner technique practice (single merged block).
 *  Fri: closed.
 */
export const DEFAULT_SCHEDULE: ScheduleTable = {
  rows: [
    {
      time: "5:00 p.m – 6:20 p.m",
      cells: {
        mon: { tone: "class", labelKeys: ["wrestling"] },
        tue: { tone: "class", labelKeys: ["wrestling"] },
        wed: {
          tone: "highlight",
          subLabel: "7:00 p.m – 9:00 p.m",
          labelKeys: ["sparring", "and", "beginnerDrills"],
          rowSpan: 4,
        },
        thu: { tone: "class", labelKeys: ["wrestling"] },
        fri: { tone: "closed", rowSpan: 4 },
      },
    },
    {
      time: "",
      cells: {
        mon: { tone: "empty" },
        tue: { tone: "empty" },
        wed: { tone: "highlight", skip: true },
        thu: { tone: "empty" },
        fri: { tone: "closed", skip: true },
      },
    },
    {
      time: "7:00 p.m – 8:20 p.m",
      cells: {
        mon: { tone: "class", labelKeys: ["wrestling"] },
        tue: { tone: "class", labelKeys: ["wrestling"] },
        wed: { tone: "highlight", skip: true },
        thu: { tone: "class", labelKeys: ["wrestling"] },
        fri: { tone: "closed", skip: true },
      },
    },
    {
      time: "8:30 p.m – 9:50 p.m",
      cells: {
        mon: { tone: "class", labelKeys: ["wrestling"] },
        tue: { tone: "class", labelKeys: ["wrestling"] },
        wed: { tone: "highlight", skip: true },
        thu: { tone: "class", labelKeys: ["wrestling"] },
        fri: { tone: "closed", skip: true },
      },
    },
  ],
};
